import { Children, cloneElement, isValidElement, PropsWithChildren, ReactElement, useCallback, useRef, useState } from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVertTwoTone';
import { Drawer, Popover, useMediaQuery, useTheme } from '@mui/material';
import { GridRowId, gridRowSelectionIdsSelector, useGridApiContext, useGridSelector } from '@mui/x-data-grid-pro';

import styles from '../RenderCell.module.css';

interface RowActionsMenuCellProps<T> extends PropsWithChildren {
	row?: T;
	rowIdProperty: keyof T;
}

/**
 * Props passed into the Menu Items
 */
export interface RowActionsMenuItemProps {
	closeSubMenu: () => void;
}

/**
 * Cell containing the action menu and actions available for the dispatch data grid. In mobile this component is used at the top of the grid.
 * The first divided section of actions are applicable to the dispatch board data grid.
 * The second divided section of actions will open a modal or navigate off the board.
 * The third divided section of actions will immediately make a data change.
 */
export default function RowActionsMenuCell<T>({ row, rowIdProperty, ...props }: RowActionsMenuCellProps<T>) {
	const cellRef = useRef<HTMLDivElement | null>(null);
	const [isOpen, setIsOpen] = useState(false);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const gridApiRef = useGridApiContext();
	const selectedRowIds = useGridSelector(gridApiRef, gridRowSelectionIdsSelector);

	/**
	 * Click event for the Row Menu Ellipsis on the row
	 */
	const onMenuClick = useCallback(() => {
		// In Desktop we should have a row prop, but in mobile we will not because the row action menu is outside the row
		if (row && !selectedRowIds.has(row[rowIdProperty] as GridRowId)) {
			gridApiRef.current.setRowSelectionModel({
				type: 'include',
				ids: new Set([...selectedRowIds.keys(), row[rowIdProperty] as GridRowId])
			});
			setIsOpen(true);
		} else if (selectedRowIds.size > 0) {
			setIsOpen(true);
		}
	}, [gridApiRef, selectedRowIds, row, rowIdProperty]);

	/**
	 * Drawer/Popover close event, preventing the menu from closing in a default manner
	 */
	function onClose(_: object, reason?: 'backdropClick' | 'escapeKeyDown') {
		if (reason !== 'backdropClick') {
			setIsOpen(true);
		} else {
			setIsOpen(false);
		}
	}

	/**
	 * Callback to allow the nested components to close the menu manually when needed
	 */
	function subMenuClose() {
		setIsOpen(false);
	}

	if (isMobile) {
		return (
			<>
				<MoreVertIcon
					className={`row-menu-btn ${styles['row-menu-btn']} ${selectedRowIds.size > 0 ? styles.active : ''}`}
					onClick={onMenuClick}
				/>
				<Drawer
					anchor='bottom'
					open={isOpen}
					onClose={onClose}
					onClick={() => {
						setIsOpen(false);
					}}
				>
					{Children.map(props.children, (child) => {
						if (isValidElement(child)) {
							return cloneElement(child as ReactElement<RowActionsMenuItemProps>, { closeSubMenu: subMenuClose });
						}
						return child;
					})}
				</Drawer>
			</>
		);
	}

	return (
		<div
			className={styles['row-menu-container']}
			ref={cellRef}
		>
			<MoreVertIcon
				className={`row-menu-btn ${styles['row-menu-btn']} ${selectedRowIds.size > 0 ? styles.active : ''}`}
				onClick={onMenuClick}
			/>
			<Popover
				open={isOpen}
				anchorEl={cellRef.current ?? null}
				transformOrigin={{
					horizontal: 'left',
					vertical: 'top'
				}}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left'
				}}
				onClose={onClose}
				className={styles['row-menu']}
				slotProps={{
					paper: {
						className: styles['row-menu-paper'],
						onClick: () => {
							setIsOpen(false);
						}
					}
				}}
				transitionDuration={100}
			>
				{Children.map(props.children, (child) => {
					if (isValidElement(child)) {
						return cloneElement(child as ReactElement<RowActionsMenuItemProps>, { closeSubMenu: subMenuClose });
					}
					return child;
				})}
			</Popover>
		</div>
	);
}
